// ─────────────────────────────────────────────────────────────────────
// Tenon · RelatedWork — the shared related-work card strip (UOW-4G #3672,
// contract docs/features/unit-of-work.md §3.11).
//
// ONE component rendered in every side peek — task/phase peek, estimate
// line peek, tracker item peek, PO/Bid/Sub peeks. Each card names a
// related record (kind + headline), carries a status pill in that
// record's own vocabulary and the money figure its surface leads with,
// opens stacked on click (the host wires the established back-stack),
// and offers a secondary "open on its own page" action. Grouped by kind
// when more than a few; absence renders NOTHING (hosts get null back —
// no empty strip, no placeholder). Cards wrap — the strip never scrolls
// horizontally, including at the 440px peek width.
//
// Data: exactly one batched call per subject (lib/relatedWork.ts), or a
// host-supplied card list mapped from an already-fetched batch (the
// estimate editor's #3587 payload). A fetch failure degrades the whole
// strip to hidden — the facet rule: never block the peek.
//
// Permission rule (#3586, enforced SERVER-side): tokens without
// estimates.view receive no estimate-line cards and no money figures, so
// this component simply renders what it is given.
// ─────────────────────────────────────────────────────────────────────

import React from 'react'
import { Icon } from '@/design-system/primitives'
import { formatCurrency } from '@/utils/formatCurrency'
// §3.13: the at-risk pill's wording comes from the ONE vocabulary the tracker
// and the task peek's Sourcing facet also read.
import { atRiskReasonLabel } from '@/lib/procurement-status'
import {
  fetchRelatedWork,
  groupRelatedCards,
  shouldGroupRelatedCards,
  relatedCardStatus,
  relatedCardMeta,
  relatedCardPageHref,
  estimateLineFigureTotal,
  RELATED_KIND_LABEL,
  RELATED_KIND_TONE,
  type RelatedWorkCard,
  type RelatedWorkSubjectType,
} from '@/lib/relatedWork'

export type { RelatedWorkCard, RelatedWorkSubjectType }

/** Open a card's record on its own page — the secondary "leave the peek"
 *  action, shared by every host (full navigation, same affordance as the
 *  tracker's View → links). */
export function openRelatedCardPage(card: RelatedWorkCard): void {
  const href = relatedCardPageHref(card)
  if (href) window.location.href = href
}

// ─── Status pill ──────────────────────────────────────────────────────

function CardStatusPill({ card }: { card: RelatedWorkCard }) {
  const status = relatedCardStatus(card)
  if (!status) return null
  const key = status.tone.replace(/_/g, '-')
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 7px', borderRadius: 'var(--r-sm)',
      background: `var(--status-${key}-bg)`, color: `var(--status-${key}-tx)`,
      fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 500,
      lineHeight: 1.2, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: `var(--status-${key}-dot)`, flexShrink: 0 }} />
      {status.label}
    </span>
  )
}

// ─── One card ─────────────────────────────────────────────────────────

function RelatedCard({ card, dead, onOpen, onOpenPage, onRemove, removing }: {
  card: RelatedWorkCard
  /** The host's open attempt failed (target unfetchable) — render inert. */
  dead: boolean
  onOpen?: (card: RelatedWorkCard) => void
  onOpenPage?: (card: RelatedWorkCard) => void
  onRemove?: () => void
  removing?: boolean
}) {
  const unavailable = !!card.unavailable || dead
  const clickable = !unavailable && !!onOpen
  const pageHref = unavailable ? null : relatedCardPageHref(card)
  const figureText = typeof card.figure === 'number'
    ? formatCurrency(card.figure, { decimals: card.kind === 'estimate_line' ? 2 : 0 })
    : null
  const meta = relatedCardMeta(card, n => formatCurrency(n, { decimals: 2 }))
  const kindTone = RELATED_KIND_TONE[card.kind]
  const label = [RELATED_KIND_LABEL[card.kind], card.number].filter(Boolean).join(' ')

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open ${RELATED_KIND_LABEL[card.kind]} ${card.headline}` : undefined}
      title={unavailable ? 'No longer available' : undefined}
      onClick={clickable ? () => onOpen!(card) : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen!(card) }
      } : undefined}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = 'var(--border-strong)' } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = 'var(--border)' } : undefined}
      style={{
        flex: '1 1 180px', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: 5,
        padding: '8px 10px', borderRadius: 'var(--r-sm)',
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        cursor: clickable ? 'pointer' : 'default',
        opacity: unavailable ? 0.6 : 1,
        transition: 'border-color 0.12s ease',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
          background: `var(--st-${kindTone}-soft)`, color: `var(--st-${kindTone})`,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          lineHeight: 1.5, whiteSpace: 'nowrap', flexShrink: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%',
        }}>{label}</span>
        <span style={{ flex: 1 }} />
        {pageHref && (
          <button
            type="button"
            aria-label={`Open ${RELATED_KIND_LABEL[card.kind]} on its own page`}
            title="Open on its own page"
            onClick={(e) => { e.stopPropagation(); onOpenPage?.(card) }}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 20, height: 20, padding: 0, border: 'none', borderRadius: 'var(--r-xs, 4px)',
              background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <Icon name="external" size={11} sw={1.8} />
          </button>
        )}
        {onRemove && !unavailable && (
          <button
            type="button"
            aria-label={`Remove ${card.headline || 'line'} from this task`}
            title="Remove this line — it gets its own task back"
            disabled={removing}
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 20, height: 20, padding: 0, border: 'none', borderRadius: 'var(--r-xs, 4px)',
              background: 'transparent', color: 'var(--text-muted)',
              cursor: removing ? 'default' : 'pointer', flexShrink: 0,
              opacity: removing ? 0.4 : 1,
            }}
          >
            <Icon name="x" size={11} sw={2} />
          </button>
        )}
      </div>
      <div style={{
        fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
      }}>
        {card.headline || 'Untitled'}
      </div>
      {meta && (
        <div style={{
          fontSize: 11, color: 'var(--text-muted)', minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{meta}</div>
      )}
      {(relatedCardStatus(card) || figureText || card.at_risk) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <CardStatusPill card={card} />
          {/* UOW-5B (§3.13): the at-risk pill on a requirement-backed card —
              this task's sourcing is late against its own schedule. Server
              verdict, one vocabulary across the tracker, the Sourcing facet
              and here. It sits beside the record's own status rather than
              replacing it: "In Progress · Order window passed" is the whole
              point, and the card only renders where the viewer can already
              open the task (§3.1/§3.11). */}
          {card.at_risk && !unavailable && (
            <span
              title={atRiskReasonLabel(card.at_risk_reason)}
              style={{
                fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 9999,
                background: 'var(--status-blocked-bg)', color: 'var(--status-blocked-tx)',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
              {atRiskReasonLabel(card.at_risk_reason)}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {figureText && (
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500,
              color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0,
            }}>{figureText}</span>
          )}
        </div>
      )}
    </div>
  )
}

// ─── The strip ────────────────────────────────────────────────────────

export interface RelatedWorkStripProps {
  cards: RelatedWorkCard[]
  /**
   * Primary click → the host opens the record as a STACKED peek (the
   * established back-stack). May return a promise; a rejection marks the
   * card unopenable and it degrades to the unavailable treatment instead
   * of offering a dead click (contract §5 archived-target rule).
   */
  onOpenCard?: (card: RelatedWorkCard) => void | Promise<void>
  /** Secondary action override; defaults to openRelatedCardPage. */
  onOpenCardPage?: (card: RelatedWorkCard) => void
  /** Per-line remove on estimate-line cards — the 4F split-a-line-out
   *  affordance the strip carries forward. Rendered only when provided
   *  AND the subject holds more than one line. */
  onRemoveLine?: (lineItemId: number) => void
  removingLineId?: number | null
  /** Show the "Estimate baseline" sum row under the estimate-line group
   *  (the combined-task rollup the 4F list displayed). */
  showEstimateBaseline?: boolean
}

export function RelatedWorkStrip({
  cards, onOpenCard, onOpenCardPage, onRemoveLine, removingLineId = null, showEstimateBaseline = false,
}: RelatedWorkStripProps) {
  // Cards whose host open attempt failed — degrade to unavailable, never a
  // repeat dead click.
  const [deadIds, setDeadIds] = React.useState<Record<string, true>>({})

  if (cards.length === 0) return null

  const handleOpen = (card: RelatedWorkCard) => {
    if (!onOpenCard) return
    try {
      const result = onOpenCard(card)
      if (result && typeof (result as Promise<void>).catch === 'function') {
        (result as Promise<void>).catch(() => {
          setDeadIds(prev => ({ ...prev, [`${card.kind}:${card.id}`]: true }))
        })
      }
    } catch {
      setDeadIds(prev => ({ ...prev, [`${card.kind}:${card.id}`]: true }))
    }
  }

  const openPage = onOpenCardPage ?? openRelatedCardPage
  // OWNED lines only: a secondary "link anyway" reference neither combines
  // the task (no baseline of its own here) nor splits (no link row to
  // remove — the server would 404 the per-line DELETE).
  const ownedLineCards = cards.filter(c => c.kind === 'estimate_line' && !c.unavailable && !c.is_secondary)
  const removable = !!onRemoveLine && ownedLineCards.length > 1
  const baseline = showEstimateBaseline && ownedLineCards.length > 1 ? estimateLineFigureTotal(cards) : null

  const renderCards = (list: RelatedWorkCard[]) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minWidth: 0 }}>
      {list.map(card => (
        <RelatedCard
          key={`${card.kind}:${card.id}`}
          card={card}
          dead={!!deadIds[`${card.kind}:${card.id}`]}
          onOpen={onOpenCard ? handleOpen : undefined}
          onOpenPage={openPage}
          onRemove={removable && card.kind === 'estimate_line' && !card.unavailable && !card.is_secondary
            ? () => onRemoveLine!(Number(card.id))
            : undefined}
          removing={removingLineId != null}
        />
      ))}
    </div>
  )

  const grouped = shouldGroupRelatedCards(cards)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: grouped ? 10 : 0, minWidth: 0 }}>
      {grouped ? (
        groupRelatedCards(cards).map(group => (
          <div key={group.kind} style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--text-muted)', marginBottom: 6,
            }}>
              {group.label} ({group.cards.length})
            </div>
            {renderCards(group.cards)}
          </div>
        ))
      ) : (
        renderCards(cards)
      )}
      {baseline != null && (
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 10, marginTop: 8, fontFamily: 'var(--font-sans)',
        }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Estimate baseline</span>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
            color: 'var(--text-primary)',
          }}>{formatCurrency(baseline, { decimals: 2 })}</span>
        </div>
      )}
      {removable && (
        <p style={{
          margin: '8px 0 0', fontFamily: 'var(--font-sans)', fontSize: 11,
          color: 'var(--text-muted)', lineHeight: 1.5,
        }}>
          Removing a line gives it its own task back on the schedule.
        </p>
      )}
    </div>
  )
}

// ─── Self-fetching section ────────────────────────────────────────────

export interface RelatedWorkSectionProps extends Omit<RelatedWorkStripProps, 'cards'> {
  subjectType: RelatedWorkSubjectType
  subjectId: string | number | null | undefined
  /** Bump to refetch (e.g. after a per-line remove or a link change). */
  refreshKey?: number
  /**
   * Section shell rendered ONLY when cards exist — absence renders nothing
   * at all, including the host's own section chrome. Default shell is a
   * plain labelled block; hosts pass their own PeekSection wrapper.
   */
  renderSection?: (content: React.ReactNode) => React.ReactNode
  /** Cards ready (or absent) callback — lets hosts derive state (e.g. the
   *  task peek's combined-line count) from the SAME single fetch. */
  onCards?: (cards: RelatedWorkCard[]) => void
}

const defaultSectionShell = (content: React.ReactNode) => (
  <section style={{ padding: '12px 0', fontFamily: 'var(--font-sans)' }}>
    <div style={{
      fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8,
    }}>
      Related work
    </div>
    {content}
  </section>
)

export function RelatedWorkSection({
  subjectType, subjectId, refreshKey = 0, renderSection = defaultSectionShell, onCards,
  ...stripProps
}: RelatedWorkSectionProps) {
  const [cards, setCards] = React.useState<RelatedWorkCard[]>([])
  const onCardsRef = React.useRef(onCards)
  onCardsRef.current = onCards

  // Subject identity — switching subjects (a host swapping POs without
  // remounting) resets the strip SYNCHRONOUSLY during render so the prior
  // subject's cards never flash, and keys the strip so its internal
  // unopenable-card state resets with it. A refetch of the SAME subject
  // (refreshKey bump after a per-line remove) keeps the current cards up
  // until the fresh set lands.
  const subjectKey = `${subjectType}:${subjectId ?? ''}`
  const [renderedSubject, setRenderedSubject] = React.useState(subjectKey)
  if (renderedSubject !== subjectKey) {
    setRenderedSubject(subjectKey)
    setCards([])
  }

  React.useEffect(() => {
    if (subjectId == null || subjectId === '') {
      setCards([])
      onCardsRef.current?.([])
      return
    }
    let cancelled = false
    fetchRelatedWork(subjectType, subjectId)
      .then(fetched => {
        if (cancelled) return
        setCards(fetched)
        onCardsRef.current?.(fetched)
      })
      .catch(() => {
        // Degrade to hidden — never block or break the peek.
        if (cancelled) return
        setCards([])
        onCardsRef.current?.([])
      })
    return () => { cancelled = true }
  }, [subjectKey, subjectType, subjectId, refreshKey])

  if (cards.length === 0) return null
  return <>{renderSection(<RelatedWorkStrip key={subjectKey} cards={cards} {...stripProps} />)}</>
}
